// Pure function: map Jabberr's participant attributes to a status description.
// The worker sets two keys on jabberr-bot:
//   lk.agent.state  = 'listening' | 'hearing' | 'thinking' | 'speaking' (or absent)
//   jabberr.heard   = identity of the person Jabberr just heard (empty otherwise)
// Returns null when the attributes object is absent (old worker).

export type JabberrAttributes = Record<string, string> | null | undefined;

export interface ParticipantsMap {
  [key: string]: { identity: string; name?: string };
}

/** How Jabberr's tile and the status bar are lit. 'speaking' uses the same glow as a person speaking;
 *  'thinking' (preparing a reply, or holding one for an opening) has its own colour so the room can
 *  tell he is about to talk; 'alert' is a turn that went wrong; 'idle' is not lit. */
export type JabberrTone = 'idle' | 'hearing' | 'thinking' | 'speaking' | 'alert';

export interface JabberrStatus {
  icon: string;
  text: string;
  tone: JabberrTone;
  extra?: string;
  mode?: 'pulsing-dots' | 'level-bars';
}

/** Human-readable status for Jabberr's current state. */
export function jabberrStatus(
  attributes: JabberrAttributes,
  participants: ParticipantsMap,
  botIdentity: string,
): JabberrStatus | null {
  if (!attributes) return null;

  const state = attributes['lk.agent.state'];
  const heard = attributes['jabberr.heard'];

  // Map heard identity to a display name.
  let heardName: string | undefined;
  if (heard) {
    const who = Object.values(participants).find(
      (p) => p.identity === botIdentity ? false : (p.identity === heard || p.name === heard),
    );
    if (who) heardName = who.name || who.identity;
  }

  // No state attribute means old worker: say nothing.
  if (!state) return null;

  switch (state) {
    case 'listening':
      return { icon: 'listening', text: 'Listening', tone: 'idle' };
    case 'hearing':
      return { icon: 'hearing', text: 'Hearing someone', tone: 'hearing', extra: heardName ? `Heard ${heardName}` : undefined };
    case 'transcribing':
      return { icon: 'hearing', text: 'Transcribing speech', tone: 'hearing', mode: 'pulsing-dots' };
    case 'unclear':
      return { icon: 'unknown', text: 'Could not make out that speech', tone: 'alert' };
    case 'thinking':
      return { icon: 'thinking', text: 'Preparing a reply', tone: 'thinking', mode: 'pulsing-dots' };
    case 'waiting':
      return { icon: 'thinking', text: 'Waiting for an opening', tone: 'thinking', mode: 'pulsing-dots' };
    case 'overloaded':
      return { icon: 'hearing', text: 'Catching up: some speech was missed', tone: 'alert' };
    case 'unavailable':
      return { icon: 'unknown', text: 'Could not finish that turn. Ask again.', tone: 'alert' };
    case 'speaking':
      return { icon: 'speaking', text: 'Speaking', tone: 'speaking', mode: 'level-bars' };
    default:
      return { icon: 'unknown', text: state, tone: 'idle' };
  }
}

/** Check if Jabberr is in the room based on remote participants. */
export function isJabberrInRoom(participants: ParticipantsMap, botIdentity: string): boolean {
  return Object.values(participants).some((p) => p.identity === botIdentity);
}
